import { useCallback, useEffect, useState } from "react";
import {
  applyTheme,
  hasStoredPreference,
  readPreference,
  resolveTheme,
  watchSystemTheme,
  writePreference,
  type ResolvedTheme,
  type ThemePreference,
} from "@/lib/theme";

interface UseTheme {
  /** What the user chose — including `system`. */
  preference: ThemePreference;
  /** What is actually on screen. Never `system`. */
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

/**
 * The app's theme state.
 *
 * Initialised from storage rather than from a default, so the value here always
 * agrees with the `data-theme` the pre-paint script already wrote — mounting
 * must not cause a flip.
 *
 * Deliberately not in Redux. Nothing else in the app reacts to the theme (the
 * stylesheet does the work), so putting it in the store would mean a slice, an
 * action and a persistence effect to move one string that one component reads.
 */
export function useTheme(): UseTheme {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readPreference()));

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writePreference(next);
    const theme = resolveTheme(next);
    setResolved(theme);
    applyTheme(theme);
  }, []);

  // Follow the OS only while the preference *is* `system`. A user who has
  // explicitly picked light should not have their panel go dark at sunset
  // because the OS did.
  useEffect(() => {
    if (preference !== "system") return;
    return watchSystemTheme((theme) => {
      setResolved(theme);
      applyTheme(theme);
    });
  }, [preference]);

  return { preference, resolved, setPreference };
}

/**
 * Adopts the theme stored on the account, once, at sign-in.
 *
 * The API keeps `theme` on the admin row so it follows a person between
 * browsers and machines — which is genuinely useful on a fresh browser and
 * genuinely annoying if it overrides the choice someone just made on the
 * machine in front of them. So it is applied **only when this browser holds no
 * preference of its own**; after that the local choice wins, and the
 * Appearance tab writes every change back to the account so the two stay in
 * step.
 *
 * `system` has no server equivalent — the account stores `light` or `dark` and
 * nothing else — so picking it is a local-only decision by definition, and one
 * this function must not undo.
 */
export function adoptAccountTheme(theme: ResolvedTheme): void {
  if (hasStoredPreference()) return;
  writePreference(theme);
  applyTheme(theme);
}
